import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Heart, Users, Target, Award } from "lucide-react";
import ClientBackground from "../components/ClientBackground";

export default function About() {
  const navigate = useNavigate();

  const values = [
    {
      icon: Heart,
      title: "Empathy First",
      description: "We understand the joys and challenges of early parenthood"
    },
    {
      icon: Users,
      title: "Community Driven",
      description: "Building connections between parents and experts worldwide"
    },
    {
      icon: Target,
      title: "Evidence-Based",
      description: "All content backed by pediatric research and expertise"
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Committed to providing the highest quality support"
    }
  ];

  const team = [
    {
      role: "Founders",
      description: "Parents and healthcare professionals passionate about early childhood development"
    },
    {
      role: "Pediatric Experts",
      description: "Board-certified doctors and child development specialists"
    },
    {
      role: "Tech Team",
      description: "Dedicated developers creating intuitive parenting solutions"
    }
  ];

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10">
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg font-semibold text-[#1e3a5f]">About TIFL</h1>
          <div className="w-10" />
        </div>

        <div className="px-5 py-6 max-w-4xl mx-auto pb-20">
          <div className="bg-white rounded-3xl p-8 shadow-lg mb-6">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#FDB913] to-[#FFA500] mb-4">
                <span className="text-4xl">👶</span>
              </div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-3">
                The First 1,000 Days
              </h2>
              <p className="text-gray-600 text-lg">
                Supporting parents through the most crucial period of child development
              </p>
            </div>

            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                TIFL was founded on the belief that every parent deserves access to expert guidance and a supportive community during their child's first 1,000 days—from pregnancy through age 3.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                This critical period shapes lifelong health, learning, and behavior. Our mission is to empower parents with tools, knowledge, and connections that make this journey less overwhelming and more joyful.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We combine cutting-edge technology with evidence-based pediatric expertise to deliver personalized milestone tracking, weekly guidance, and a warm community of parents who understand exactly what you're going through.
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-2xl font-bold text-[#1e3a5f] mb-6 text-center">
              Our Values
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#7FE5D3] to-[#5FD3C1] flex items-center justify-center">
                      <value.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1e3a5f] mb-1">
                        {value.title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-2xl font-bold text-[#1e3a5f] mb-6 text-center">
              Our Team
            </h3>
            <div className="space-y-4">
              {team.map((member, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-sm"
                >
                  <h4 className="font-semibold text-[#1e3a5f] mb-2">
                    {member.role}
                  </h4>
                  <p className="text-gray-600">
                    {member.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2a4b73] rounded-3xl p-8 text-white text-center shadow-lg">
            <h3 className="text-2xl font-bold mb-3">
              Join Our Community
            </h3>
            <p className="mb-6 opacity-90">
              Be part of thousands of parents supporting each other through the incredible first 1,000 days
            </p>
            <button
              onClick={() => navigate("/client-dashboard")}
              className="px-8 py-3 bg-white text-[#1e3a5f] rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}